import React from 'react';

import type { PlanConfig } from '../services/subscriptionService';
import { formatDailyCredits, formatPlanCredits, getPlanCreditSummary } from '../utils/planCreditSummary';
import type { PlanCreditSummary } from '../utils/planCreditSummary';

import './PlanCreditBreakdown.css';

const DailyCredits: React.FC<{ summary: PlanCreditSummary }> = ({ summary }) => {
  if (summary.boostDaily && summary.boostDays) {
    return (
      <>
        {formatPlanCredits(summary.boostDaily)}<span>per day for {summary.boostDays} days, then</span>
        {' '}{formatPlanCredits(summary.daily)}<span>per day</span>
      </>
    );
  }

  if (summary.daily <= 0) return <>None</>;
  const approximate = summary.approximateDaily === true;

  return (
    <>
      {approximate ? '~' : ''}{formatPlanCredits(approximate ? Math.round(summary.daily) : summary.daily)}
      <span>{approximate ? 'per day, for the rest of the billing month' : 'per day'}</span>
    </>
  );
};

/** February cycles end one or two tail grants early: "48–49" for the free plan. */
const februaryRange = (summary: PlanCreditSummary): string => (
  `${formatPlanCredits(summary.total - (2 * summary.daily))}–${formatPlanCredits(summary.total - summary.daily)}`
);

const BreakdownNote: React.FC<{ summary: PlanCreditSummary }> = ({ summary }) => {
  if (summary.expiresEachCycle) {
    return (
      <p className="plan-credit-breakdown__note">
        A full {summary.cycleDays ?? 30}-day cycle gives {formatPlanCredits(summary.total)}. A 31st day adds none,
        and February cycles give {februaryRange(summary)}. Your cycle restarts every month from your signup date;
        unused free credits expire when it restarts. Purchased credits never expire.
      </p>
    );
  }
  if (summary.daily <= 0) return null;

  return (
    <p className="plan-credit-breakdown__note">
      {summary.annual
        ? 'Half of the year\'s credits arrive when you pay. The rest is added in equal daily amounts through each month.'
        : 'Half arrives when you pay. The rest is added in equal daily amounts over the rest of the billing month.'}
    </p>
  );
};

/** One-line delivery summary shown under the lead number. */
const deliveryLine = (summary: PlanCreditSummary): string => {
  if (summary.unlimited) return 'No wallet deductions while the plan is active';
  if (summary.signupOnly && summary.boostDaily && summary.boostDays && summary.boostDaily === summary.upfront) {
    return `${formatPlanCredits(summary.boostDaily)} / day for ${summary.boostDays + 1} days, then `
      + `${formatPlanCredits(summary.daily)} / day to day ${summary.cycleDays ?? 30}`;
  }
  const upfront = `${formatPlanCredits(summary.upfront)} ${summary.signupOnly ? 'on signup' : 'upfront'}`;

  return summary.daily > 0 ? `${upfront} + ${formatDailyCredits(summary)}` : upfront;
};

const DeliveryDetails: React.FC<{ summary: PlanCreditSummary }> = ({ summary }) => {
  const upfrontWhen = summary.signupOnly ? 'on signup, then monthly' : summary.annual ? 'each year' : 'each month';

  return (
    <details className="plan-credit-breakdown__details-toggle">
      <summary>How credits arrive</summary>
      <dl className="plan-credit-breakdown__details">
        <div><dt>Upfront credits</dt><dd>{formatPlanCredits(summary.upfront)}<span>{upfrontWhen}</span></dd></div>
        <div><dt>Additional daily credits</dt><dd><DailyCredits summary={summary} /></dd></div>
      </dl>
      <BreakdownNote summary={summary} />
    </details>
  );
};

export const PlanCreditBreakdown: React.FC<{
  planKey: string;
  plan: PlanConfig;
  isAnnual?: boolean;
}> = ({ planKey, plan, isAnnual = false }) => {
  const summary = getPlanCreditSummary(planKey, plan, isAnnual);

  if (!summary) return null;
  const period = summary.signupOnly ? '/ month' : summary.annual ? '/ year' : '/ month';

  return (
    <section className="plan-credit-breakdown" aria-label={`${plan.name} included credits`}>
      <div className="plan-credit-breakdown__total">
        <strong>{summary.unlimited ? 'Unlimited' : formatPlanCredits(summary.total)}</strong>
        <span>{summary.unlimited ? 'credits' : `credits ${period}`}</span>
      </div>
      <p className="plan-credit-breakdown__delivery">{deliveryLine(summary)}</p>
      {!summary.unlimited && <DeliveryDetails summary={summary} />}
    </section>
  );
};
